import { DateTime } from 'luxon';
import type { JSX } from 'react';

import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { isDefined, isUndefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { Translation } from '../../../common/i18n/Text.ts';
import { ButtonSpecialState } from '../../../ui/buttons/IconButton.tsx';
import { LabeledText } from '../../../ui/labeled-elements/LabeledText.tsx';
import { TextField } from '../../../ui/labeled-elements/TextField.tsx';
import {
    ConfirmSaveHeader,
    HalfSlideIn,
    LoaderHeader,
    SlideInBody,
} from '../../../ui/slidein/Slidein.tsx';
import * as OutOfServiceActions from '../../actions/OutOfServiceActions.ts';
import { outOfServiceTexts } from '../../i18n/TomTexts.ts';
import * as TomLayoutState from '../../state/TomLayoutState.ts';
import * as TomState from '../../state/TomState.ts';
import { OutOfServiceDateSelection } from './OutOfServiceDateHalfSlideIn.tsx';
import { OutOfServiceMessage } from './OutOfServiceMessage.tsx';
import { css } from '@emotion/css';

export interface OutOfServiceTexts {
    OutOfService: Translation;
    PlanOutOfService: Translation;
    OutOfServiceDateSlideInTitle: Translation;
    StartDate: Translation;
    EndDate: Translation;
    MessageDateConstraints: Translation;
    CustomMessageToggle: Translation;
    MessageTitle: Translation;
    MessageTitleConstraints: Translation;
    MessageBody: Translation;
    MessageBodyConstraints: Translation;
    OutOfServiceMessagePreview: Translation;
    OutOfServiceTitleDefault: Translation;
    OutOfServiceBodyDefault: Translation;
}

export function OutOfServiceHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: TomLayoutState.Layout.get(store),
        form: TomState.OutOfService.Form.get(store),
        response: TomState.OutOfService.Response.get(store),
    }));
    const { language } = useLanguage();

    function isStartDateValid(): boolean {
        return (
            isDefined(storeState.form.startDate) &&
            storeState.form.startDate > DateTime.now()
        );
    }

    function isEndDateValid(): boolean {
        // in current implementation both are required. otherwise it would be impossible to
        // reactivate a tom if no end date was selected
        return (
            isDefined(storeState.form.endDate) &&
            isDefined(storeState.form.startDate) &&
            storeState.form.endDate > storeState.form.startDate
        );
    }

    function isTitleValid(): boolean {
        // max 2 lines of 15 chars each or empty
        if (isUndefined(storeState.form.title)) {
            return true;
        }
        const lines = storeState.form.title.split('\n');
        return lines.length <= 2 && lines.every(l => l.length <= 15);
    }

    function isBodyValid(): boolean {
        // max 3 lines of 24 chars each or empty
        if (isUndefined(storeState.form.body)) {
            return true;
        }
        const lines = storeState.form.body.split('\n');
        return lines.length <= 3 && lines.every(l => l.length <= 24);
    }

    function isFormValid(): boolean {
        return (
            isStartDateValid() &&
            isEndDateValid() &&
            isTitleValid() &&
            isBodyValid()
        );
    }

    function confirmButtonSpecialState(): Maybe<ButtonSpecialState> {
        if (storeState.response.pending) {
            return ButtonSpecialState.DISABLED;
        }
        if (
            !isFormValid() &&
            storeState.layout.outOfServiceConfirmPressedOnError
        ) {
            return ButtonSpecialState.ERROR;
        }
        if (!isFormValid()) {
            return ButtonSpecialState.DISABLED;
        } else {
            return null;
        }
    }

    function renderHeader(): JSX.Element {
        const lng = language;
        const txt = outOfServiceTexts[lng];

        if (storeState.response.pending) {
            return <LoaderHeader title={txt.PlanOutOfService()} />;
        }

        return (
            <ConfirmSaveHeader
                language={language}
                title={txt.PlanOutOfService()}
                onCancel={() => update(OutOfServiceActions.cancelOutOfService)}
                onSave={() => {
                    if (!isFormValid() || !storeState.layout.selectedTomId) {
                        update(store =>
                            TomLayoutState.Layout.stateWrite(store, {
                                outOfServiceConfirmPressedOnError: true,
                            }),
                        );
                    } else {
                        update(OutOfServiceActions.setOutOfService, {
                            tomId: storeState.layout.selectedTomId,
                            startDate: Formatter.isoYearMonthDay(
                                storeState.form.startDate ||
                                    DateTime.fromISO('1970-01-01'),
                            ),
                            endDate: Formatter.isoYearMonthDay(
                                storeState.form.endDate ||
                                    DateTime.fromISO('1970-01-01'),
                            ),
                            title: storeState.form.title,
                            body: storeState.form.body,
                        });
                    }
                }}
                confirmButtonSpecialState={confirmButtonSpecialState()}
            />
        );
    }

    const lng = language;
    const txt = outOfServiceTexts[lng];

    if (storeState.layout.outOfServiceOpen) {
        const showErrors = storeState.layout.outOfServiceConfirmPressedOnError;
        return (
            <HalfSlideIn open={true}>
                <SlideInBody>
                    <div>
                        <OutOfServiceDateSelection
                            isValid={isStartDateValid() && isEndDateValid()}
                            showErrors={showErrors}
                        />

                        <div
                            className={css({
                                textarea: {
                                    height: '48px',
                                },
                            })}
                        >
                            <TextField
                                value={storeState.form.title || ''}
                                label={txt.MessageTitle()}
                                onChange={v =>
                                    update(store =>
                                        TomState.OutOfService.Form.stateWrite(
                                            store,
                                            { title: v as string },
                                        ),
                                    )
                                }
                                inputType="text"
                                multiline={true}
                                errorText={
                                    showErrors && !isTitleValid()
                                        ? txt.MessageTitleConstraints()
                                        : null
                                }
                            />
                        </div>
                        <div
                            className={css({
                                textarea: {
                                    height: '72px',
                                },
                            })}
                        >
                            <TextField
                                value={storeState.form.body || ''}
                                label={txt.MessageBody()}
                                onChange={v =>
                                    update(store =>
                                        TomState.OutOfService.Form.stateWrite(
                                            store,
                                            { body: v as string },
                                        ),
                                    )
                                }
                                inputType="text"
                                multiline={true}
                                errorText={
                                    showErrors && !isBodyValid()
                                        ? txt.MessageBodyConstraints()
                                        : null
                                }
                            />
                        </div>
                        <div className={css({ marginTop: '20px' })}>
                            <LabeledText
                                label={txt.OutOfServiceMessagePreview()}
                            >
                                <OutOfServiceMessage
                                    title={storeState.form.title}
                                    body={storeState.form.body}
                                    language={lng}
                                />
                            </LabeledText>
                        </div>
                    </div>
                </SlideInBody>
                {renderHeader()}
            </HalfSlideIn>
        );
    } else {
        return <HalfSlideIn open={false} />;
    }
}
